import {
  asDraggableData,
  destinationFor,
  droppableId,
  type DroppableData,
} from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useTransferDropHighlight } from "@/dnd/useTransferDropHighlight";
import { ChatsRailDraggableChatRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/ChatsRailDraggableChatRow";
import { FilesRailChatContextBody } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailContextSection";
import { useRailOsDropHighlightId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailFilesDropHighlight";
import { RailOsFileDropZone } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailOsFileDropZone";
import { useRailScopeOsDrop } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useRailScopeOsDrop";
import {
  RAIL_LIST_ITEM_ATTR,
  RAIL_SELECTED_ATTR,
  useRailRowSelection,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import type { InboxItem } from "@cohesive/api-client";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { useMemo } from "react";

/**
 * The chat node as a file drop target (add a file into this chat — copy into
 * context). Wraps the whole node (header + expanded files). Reuses the
 * `chat-composer` dispatch (copies into the chat by id) via a distinct id so it
 * never collides with an open chat page. OS file drops upload straight into
 * the chat's scope.
 *
 * It is a FILE-only zone, so it stays **disabled** while dragging a canvas or
 * chat — a file zone must never intercept a canvas/chat drag meant for the
 * section or a folder.
 *
 * Pass `selectionHost` when this zone is a Workspaces-rail chat list sibling
 * so contiguous multi-select chrome keys off the drop-zone, not the nested row.
 */
export function ChatRowDropZone({
  flowSessionId,
  selectionHost,
  children,
}: {
  flowSessionId: string;
  selectionHost?: { section: RailSelectionSource };
  children: React.ReactNode;
}) {
  const { active } = useDndContext();
  const activeIsFiles =
    !!active && asDraggableData(active.data.current)?.kind === "files";

  const { selected } = useRailRowSelection(
    flowSessionId,
    selectionHost?.section ?? "canonical",
    false,
    "chat",
    false,
  );

  const dropId = droppableId.railChatRow(flowSessionId);
  const droppableData = useMemo(
    (): DroppableData => ({
      surface: "chat-composer",
      destination: destinationFor("chat-composer", { flowSessionId }),
    }),
    [flowSessionId],
  );
  const droppable = useDroppable({
    id: dropId,
    data: droppableData,
    disabled: !activeIsFiles,
  });
  const { isOver, disallowed } = useTransferDropHighlight(
    dropId,
    droppableData.destination,
  );

  const handleOsDrop = useRailScopeOsDrop({ flowSessionId });
  const osActiveDropId = useRailOsDropHighlightId();
  const osOver = osActiveDropId === dropId;

  return (
    <RailOsFileDropZone
      ref={droppable.setNodeRef}
      dropId={dropId}
      onDrop={handleOsDrop}
      className={cnTransferDropTargetSurface({
        isOver: isOver || osOver,
        disallowed,
      })}
      {...(selectionHost
        ? {
            [RAIL_LIST_ITEM_ATTR]: "",
            ...(selected ? { [RAIL_SELECTED_ATTR]: "" } : {}),
          }
        : {})}
    >
      {children}
    </RailOsFileDropZone>
  );
}

/**
 * A chat inside the workspace containment tree — the §5.1 leaf that expands to
 * reveal its files directly (a flat, backend-driven list). The header is the
 * normal draggable chat row (its title still opens the chat); the leading icon
 * doubles as the disclosure toggle. Children mount lazily on expand.
 */
export function WorkspacesRailChatNode({
  item,
  dragNamespace,
  section = "canonical",
  active,
  onOpen,
  onToggleStar,
  onRename,
  onDelete,
}: {
  item: InboxItem;
  dragNamespace: string;
  section?: RailSelectionSource;
  active?: boolean;
  onOpen: (item: InboxItem) => void;
  onToggleStar?: (item: InboxItem) => void;
  onRename: (item: InboxItem) => void;
  onDelete: (item: InboxItem) => void;
}) {
  const [expanded, setExpanded] = useRailExpansion(
    `node:${dragNamespace}:${item.flow_session_id}`,
  );

  return (
    <ChatRowDropZone
      flowSessionId={item.flow_session_id}
      selectionHost={{ section }}
    >
      <div className="flex flex-col">
        <ChatsRailDraggableChatRow
          item={item}
          dragNamespace={dragNamespace}
          section={section}
          active={active}
          listItem={false}
          showActionsMenu
          onOpen={onOpen}
          onToggleStar={onToggleStar}
          onRename={onRename}
          onDelete={onDelete}
          expanded={expanded}
          onToggleExpanded={() => setExpanded(!expanded)}
        />
        {expanded && (
          // pl-2: the rail's uniform 8px step per level, same as
          // WorkspacesRailCanvasNode — the file rows' phantom chevron spacer
          // gives them the same internal 28px icon offset as this row, so
          // their icons land exactly 8px deeper than the chat's
          // MessagesSquare icon. No gap — selection highlight bands must
          // stay contiguous.
          <div className="flex flex-col pl-2">
            <FilesRailChatContextBody
              flowSessionId={item.flow_session_id}
              withChevronSpacer
            />
          </div>
        )}
      </div>
    </ChatRowDropZone>
  );
}
